import { ComponentPublicInstance, Directive, DirectiveBinding } from 'vue';
import { Nullish } from '@/shared/utils/typescript';
import CTooltipListContent from '../components/tooltip/CTooltipListContent.vue';
import CTooltipTextContent from '../components/tooltip/CTooltipTextContent.vue';
import { TooltipContent, TooltipTheme } from '../models/TooltipInfo';
import TooltipService from '../services/TooltipService';

const TOOLTIP_DELAY_MS = 500;
let showTid: ReturnType<typeof setTimeout>;

class Controller {
  private content: TooltipContent | undefined;
  private active = false;
  private theme: TooltipTheme = 'neutral';

  private static map = new Map<EventTarget, Controller>();

  private constructor(
    private el: HTMLElement,
  ) {
  }

  static create(el: HTMLElement): Controller {
    const controller = new Controller(el);
    Controller.map.set(el, controller);
    return controller;
  }

  static get(el: Nullish<EventTarget>): Controller | undefined {
    return el ? Controller.map.get(el) : undefined;
  }

  static remove(el: EventTarget): void {
    Controller.map.get(el)?.hide();
    Controller.map.delete(el);
  }

  static hideAll(): void {
    for (const controller of Controller.map.values()) controller.hide();
  }

  updateBinding(binding: DirectiveBinding): void {
    this.content = this.normalizeContent(binding.value);
    this.theme = binding.modifiers.neutral ? 'neutral' : 'contrast';
    if (this.active) this.show();
  }

  normalizeContent(value: any): TooltipContent | undefined {
    if (!value) {
      return undefined;
    }
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return undefined;
      } else {
        return { component: CTooltipListContent, props: { items: value } };
      }
    }
    if (typeof value === 'object' && 'component' in value) {
      return value;
    }
    return { component: CTooltipTextContent, props: { content: value.toString() } };
  }

  show(): void {
    if (!this.content) return this.hide();
    TooltipService.show(this.content, this.el, this.theme);
    this.active = true;
  }

  hide(): void {
    if (this.active) {
      TooltipService.hide();
      this.active = false;
    }
  }
}

function onMouseEnter(ev: MouseEvent): void {
  onMouseLeave(ev);
  showTid = setTimeout(() => Controller.get(ev.target)?.show(), TOOLTIP_DELAY_MS);
}

function onMouseLeave(ev: Event): void {
  ev.stopPropagation();
  clearTimeout(showTid);
  Controller.get(ev.target)?.hide();
}

function isInsideTooltip(component: Nullish<ComponentPublicInstance>): boolean {
  if (!component) return false;
  return component.$el?.classList?.contains('c-tooltip') || isInsideTooltip(component.$parent);
}

document.addEventListener('scroll', Controller.hideAll, true);

const directive: Directive = {
  beforeMount(el, binding) {
    if (isInsideTooltip(binding.instance)) return;
    Controller.create(el).updateBinding(binding);
    el.addEventListener('mouseenter', onMouseEnter);
    el.addEventListener('mouseleave', onMouseLeave);
  },

  updated(el, binding) {
    Controller.get(el)?.updateBinding(binding);
  },

  unmounted(el) {
    el.removeEventListener('mouseenter', onMouseEnter);
    el.removeEventListener('mouseleave', onMouseLeave);
    Controller.remove(el);
  },
};

export default directive;
